import type { Theme } from '@mui/material';
import { AppBar, Badge, Box, Button, Divider, IconButton, Toolbar } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { FC } from 'react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { appInfo } from '../../config';
import { useSettings } from '../../contexts/settings-context';
import { ChevronDown as ChevronDownIcon } from '../../icons/chevron-down';
import { Moon as MoonIcon } from '../../icons/moon';
import { Sun as SunIcon } from '../../icons/sun';
import SentryReportBugButton from '../atoms/sentry-report-bug-button';
import { LanguagePopover } from '../molecules/language-popover';

import AccountPopover from './account-popover';
import { NavbarMenu } from './navbar-menu';

import { OrganizationLanguage } from '@/features/settings/api/types/gdpr';
import MarketplaceTrigger from '../molecules/marketplace-trigger';

export const Navbar: FC = () => {
  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const { i18n, t } = useTranslation(['common']);
  const { settings, saveSettings } = useSettings();
  const [openMenu, setOpenMenu] = useState(false);
  const [darkMode, setDarkMode] = useState(settings.theme === 'dark');

  const handleLanguageChange = (language: 'en' | 'pl'): void => {
    i18n.changeLanguage(language);
    saveSettings({
      ...settings,
      language,
    });

    toast.success(t('common:languageChanged'));
  };

  const handleSwitchTheme = (): void => {
    saveSettings({
      ...settings,
      theme: settings.theme === 'light' ? 'dark' : 'light',
    });

    setDarkMode(settings.theme === 'light');
  };

  return (
    <AppBar elevation={0} sx={{ backgroundColor: '#1e212a' }}>
      <Toolbar
        disableGutters
        sx={{
          alignItems: 'center',
          display: 'flex',
          minHeight: 64,

          pr: 2,
          py: 1,
        }}>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            width: '72px',
            transition: '0.3s',
            position: 'relative',
            '&:hover .apps': {
              visibility: 'visible',
              opacity: 1,
            },
          }}>
          <Box
            className="logo"
            sx={{ transition: '0.3s', height: '100%', display: 'flex', alignItems: 'center' }}>
            <img src={appInfo.icon} alt="logo" style={{ width: '48px' }} />
          </Box>
        </Box>
        <Divider
          flexItem
          orientation="vertical"
          sx={{
            borderColor: 'rgba(255,255,255,0.1)',
            mr: 3,
          }}
        />
        <Box>
          {appInfo.versionType !== 'PROD' ? (
            <Badge color="primary" badgeContent={`${appInfo.versionType}`}>
              {mdDown ? appInfo.shortCut : appInfo.fullName}
            </Badge>
          ) : (
            appInfo.fullName
          )}
        </Box>
        <NavbarMenu onClose={() => setOpenMenu(false)} open={mdDown && openMenu} />
        <Button
          endIcon={
            <ChevronDownIcon
              fontSize="small"
              sx={{
                ml: 2,
                transition: 'transform 250ms',
                transform: openMenu ? 'rotate(180deg)' : 'none',
              }}
            />
          }
          onClick={() => setOpenMenu(true)}
          sx={{
            color: 'primary.contrastText',
            display: {
              md: 'none',
              xs: 'flex',
            },
            fontSize: '16px',
          }}
          variant="text">
          Menu
        </Button>

        <Box sx={{ flexGrow: 1 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <LanguagePopover
            language={i18n.language as OrganizationLanguage}
            onLanguageChange={handleLanguageChange}
            sx={{
              display: {
                md: 'inline-flex',
                xs: 'none',
              },
            }}
          />

          <IconButton
            color="inherit"
            onClick={handleSwitchTheme}
            sx={{
              display: {
                md: 'inline-flex',
                xs: 'none',
              },
            }}>
            {darkMode ? (
              <SunIcon sx={{ width: 20, height: 20 }} />
            ) : (
              <MoonIcon sx={{ width: 20, height: 20 }} />
            )}
          </IconButton>

          <SentryReportBugButton />

          <MarketplaceTrigger />
        </Box>
        <AccountPopover
          darkMode={darkMode}
          onLanguageChange={handleLanguageChange}
          onSwitchTheme={handleSwitchTheme}
        />
      </Toolbar>
    </AppBar>
  );
};
